import semver from 'semver'
import isDesktopApp from '@/desktop-app/isDesktopApp'
import {getIconFromCurrency, getSymbolFromCurrency} from '@/lib/currency/currency'
import {getContrastColor} from '@/lib/color'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import ThemeMode from '@/mixins/themeMode'
import {arrayUniqueByObjectKey, clone} from '@/lib/helper/helper'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum.js'
import AddressTypeEnum from '@/api/Enums/AddressTypeEnum.js'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'

const LOGO_TYPE = {
  GMAP  : 'gmap',
  LAZY  : 'lazy',
  QRCODE: 'qrcode',
  MAIN  : 'main'
}
const LOGO_COLOR = {
  LIGHT: 'light',
  DARK : 'dark'
}

export default {
  mixins: [ThemeMode],

  data() {
    return {
      LOGO_TYPE : LOGO_TYPE,
      LOGO_COLOR: LOGO_COLOR
    }
  },

  methods: {
    getMeasurementUnitById(id) {
      if (!id) return null

      return this.$t('Restaurant.MenuAddDialog.Field.Unit.Units')?.find(item => item.Id === id) || null // this.appConfig?.MEASUREMENT_UNITS?.find(item => item.Id === id) || null
    },
    getLanguageByCode(langCode) {
      return this.selectedLanguages.find(lang => lang.Code === langCode)
    },
    getContrastLogo(type, color) {
      const logoContrast = color === LOGO_COLOR.LIGHT || color === LOGO_COLOR.DARK ? color : !color ? LOGO_COLOR.LIGHT : getContrastColor(color) === 'white' ? LOGO_COLOR.LIGHT : LOGO_COLOR.DARK
      if (type === LOGO_TYPE.GMAP) return this.getGMapIcon(logoContrast)
      if (type === LOGO_TYPE.LAZY) return this.getLazyLogo(logoContrast)
      if (type === LOGO_TYPE.QRCODE) return this.getQRLogo(logoContrast)
      if (type === LOGO_TYPE.MAIN) return this.getLogo(logoContrast)
      return this.getLogo(logoContrast)
    },
    getGMapIcon(type = LOGO_COLOR.LIGHT) {
      return type === LOGO_COLOR.DARK ? this.appConfig.LOCATION_DATA.GMapIconDark : this.appConfig.LOCATION_DATA.GMapIcon
    },
    getLazyLogo(type = LOGO_COLOR.LIGHT) {
      return type === LOGO_COLOR.DARK ? this.appConfig.LOCATION_DATA.LazyLogoDark : this.appConfig.LOCATION_DATA.LazyLogo
    },
    async getQRLogo(type = LOGO_COLOR.LIGHT) {
      return type === LOGO_COLOR.DARK ? this.appConfig.LOCATION_DATA.QrLogoDark : this.appConfig.LOCATION_DATA.QrLogo
    },
    getLogo(type = LOGO_COLOR.LIGHT) {
      return type === LOGO_COLOR.DARK ? this.appConfig.LOCATION_DATA.LogoDark : this.appConfig.LOCATION_DATA.Logo
    },

    getWebEcrTimeout(TerminalVendorId) {
      return this.appConfig?.POS_VENDORS?.find(vendor => vendor.Id === TerminalVendorId)?.WebEcrTimeout || parseInt(import.meta.env.VITE_API_TIMEOUT)
    }
  },
  watch   : {},
  computed: {
    appConfig: {
      get() {
        return window.Vue?.AppConfig || window.appConfig
      },
      set(val) {
        window.appConfig = val
        window.Vue.AppConfig = val
      }
    },
    appVersion() {
      return 'v' + semver.valid(semver.coerce(this.$DataStore?.app?.versionWeb)) || ''
    },
    appVersionDesktop() {
      return isDesktopApp() ? 'v' + semver.valid(semver.coerce(this.$DataStore?.app?.versionDesktop)) : ''
    },

    isDevelopment() {
      return import.meta.env.MODE === 'development'
    },

    isStaging() {
      return import.meta.env.MODE === 'staging'
    },

    isProduction() {
      return import.meta.env.MODE === 'production'
    },

    canSeeActiveElectronWorkingStations() {
      return this.appConfig?.LOCATION_DATA?.WaiterCanSeeElectronActive ?? false
    },

    hasActiveElectronWorkingStation() {
      const electronGuids = this.$DataStore?.app?.electronGuidActive || []
      const appMachines = this.appMachines
      const requiredMachines = appMachines?.filter(machine => machine.print_required) || []
      const allRequiredMachinesActive = requiredMachines.every(machine => electronGuids.includes(machine.guid))
      const activeMachines = requiredMachines?.filter(machine => electronGuids.includes(machine.guid))?.map(machine => machine.alias ? machine.alias : machine.hostname) || []
      const missingMachines = requiredMachines?.filter(machine => !electronGuids.includes(machine.guid))?.map(machine => machine.alias ? machine.alias : machine.hostname) || []

      return {
        AllActive      : allRequiredMachinesActive,
        ActiveMachines : activeMachines,
        MissingMachines: missingMachines
      }
    },

    isDarkMode() {
      return this.$vuetify.theme.dark
    },

    orderTypeDefaultTimes: {
      get() {
        return window.Vue.OrderTypeDefaultTimes
      },
      set(val) {
        window.Vue.OrderTypeDefaultTimes = val
      }
    },

    myDataStatus: {
      get() {
        return window.Vue.MyDataStatus
      },
      set(val) {
        window.Vue.MyDataStatus = val
      }
    },

    documentsStatus: {
      get() {
        return window.Vue.DocumentsStatus
      },
      set(val) {
        window.Vue.DocumentsStatus = val
      }
    },

    onlineOrderingStatus: {
      get() {
        return window.Vue.AppOnlineOrderingStatus
      },
      set(val) {
        window.Vue.AppOnlineOrderingStatus = val
      }
    },

    appOnlineStatus: {
      get() {
        return window.Vue.AppOnlineStatus
      },
      set(val) {
        window.Vue.AppOnlineStatus = val
      }
    },

    appOnlineTotalUsers() {
      return {
        users: [...this.appOnlineNormalUsers.users, ...this.appOnlineGuestUsers.users],
        count: (this.appOnlineNormalUsers.count + this.appOnlineGuestUsers.count)
      }
    },

    appOnlineTotalUsersUnique() {
      const users = arrayUniqueByObjectKey([...this.appOnlineNormalUsers.users, ...this.appOnlineGuestUsers.users], 'uid')
      const count = users.length

      return {
        users: users,
        count: count
      }
    },

    appOnlineNormalUsers() {
      return this.$DataStore.channelConnections.normal
    },

    appOnlineGuestUsers() {
      return this.$DataStore.channelConnections.guest
    },

    appOnlineAdminUsers() {
      return this.$DataStore.channelConnections.admin
    },

    appCountryCode() {
      return this.appConfig.LOCATION_DATA?.CountryCode?.toUpperCase() || 'GR'
    },

    appCountryId() {
      return this.appConfig.LOCATION_DATA?.CountryId || 86
    },

    appCountryIsGreece() {
      return this.appCountryCode === 'GR'
    },
    appCountryIsCyprus() {
      return this.appCountryCode === 'CY'
    },

    appSupportsEmailNotifications() {
      return true
    },
    appSupportsSmsNotifications() {
      return false
    },
    appSupportsPushNotifications() {
      return false
    },

    appActiveCatalogs() {
      return this.appConfig?.LOCATION_DATA?.Catalogs?.filter(catalog => catalog.Status && catalog.CanCreateOrder) || []
    },

    appSupportIncomeClassifications() {
      return this.appCountryIsGreece
    },
    appSupportSecondTax() {
      return this.appCountryIsGreece && this.posUserCan('Tables', 'ACCESS')
    },
    appTaxes() {
      return this.appConfig?.TAX || {}
    },
    appHasOrphanedTax() {
      return this.appTaxes?.Orphaned?.Category?.Count > 0 || this.appTaxes?.Orphaned?.Menu?.Count > 0
    },

    appHasMarketingDocsEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.MarketingDocumentEnabled
    },

    appHasFiscalDocsEnabled() {
      return this.userCanViewFiscalDoc
    },

    appHasReceiptDocEnabled() {
      return this.appHasReceiptSaleDocEnabled || this.appHasReceiptServiceDocEnabled || this.appHasReceiptCollectionDocEnabled
    },
    appHasReceiptSaleDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsEnabled
    },
    appHasReceiptSelfSupplyDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsSelfSupplyEnabled
    },
    appHasReceiptSelfUseDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsSelfUseEnabled
    },
    appHasReceiptServiceDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsServiceEnabled
    },
    appHasReceiptCollectionDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsCollectionEnabled
    },

    appHasInvoiceDocEnabled() {
      return this.appHasInvoiceSaleDocEnabled || this.appHasInvoiceServiceDocEnabled
    },
    appHasInvoiceSaleDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsInvoiceEnabled
    },
    appHasInvoiceServiceDocEnabled() {
      return !!this.appConfig?.LOCATION_DATA?.ReceiptsServiceInvoiceEnabled
    },

    appHasFnbDocEnabled() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsFnbEnabled ?? false
    },

    appCanConvertReceiptToInvoice() {
      return this.posUserCan('RECEIPTS', 'MANAGE')
        && this.appHasFiscalDocsProvider
        && this.appHasInvoiceDocEnabled
        && this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD
        && this.appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE)
    },

    ReceiptsNotify() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsNotify || []
    },
    ReceiptsPrintNotify() {
      return this.ReceiptsNotify.includes(NotifyTypeEnum.PRINT)
    },
    ReceiptsEmailNotify() {
      return this.ReceiptsNotify.includes(NotifyTypeEnum.EMAIL) && this.appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)
    },
    ReceiptsInvoiceNotify() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsInvoiceNotify || []
    },
    ReceiptsInvoicePrintNotify() {
      return this.ReceiptsInvoiceNotify.includes(NotifyTypeEnum.PRINT)
    },
    ReceiptsInvoiceEmailNotify() {
      return this.ReceiptsInvoiceNotify.includes(NotifyTypeEnum.EMAIL) && this.appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)
    },
    appHasFiscalDocsProvider() {
      return !!this.appFiscalDocsProvider
    },
    appFiscalDocsProvider() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsProvider || ''
    },
    appFiscalDocsProviderType() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsProviderType || FiscalDocsProviderTypeEnum.NONE
    },
    appFiscalDocsProviderNotify() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsProviderNotify || []
    },
    appFiscalDocsProviderDocument() {
      return this.appConfig?.LOCATION_DATA?.ReceiptsProviderDocument || []
    },
    appFiscalDocsProviderCloseFnbDocuments() {
      return {
        In    : this.appConfig?.LOCATION_DATA?.ReceiptsProviderCloseFnbDocumentsIn ?? 20,
        InInfo: this.appConfig?.LOCATION_DATA?.ReceiptsProviderCloseFnbDocumentsInInfo ?? 6
      }
    },

    appHasWebhooksOrderActive() {
      return !!this.appConfig?.LOCATION_DATA?.WebhooksOrderActive
    },

    maxCashPaymentAmount() {
      return 500
    },

    minSelfDeliveryPaymentAmount() {
      return 0.10
    },

    appNotifyPush() {
      return this.appConfig?.LOCATION_DATA?.Notify?.Push || {}
    },
    appNotifyPushActive() {
      return this.appNotifyPush?.Active && this.appNotifyPush?.Push?.Active && (this.appNotifyPush?.Push?.Web.Active || this.appNotifyPush?.Push?.Mobile.Active)
    },

    appNotifySms() {
      return this.appConfig?.LOCATION_DATA?.Notify?.Sms || {}
    },
    appNotifySmsActive() {
      return this.appNotifySms?.active ?? false
    },
    appNotifySmsSender() {
      return this.appNotifySms?.from || ''
    },
    appNotifySmsCredits() {
      return this.appConfig?.LOCATION_DATA?.Notify?.SmsCredits || 0
    },
    appNotifySmsColor() {
      return this.appNotifySmsCredits <= 50 ? 'red' : this.appNotifySmsCredits <= 300 ? 'orange' : this.appNotifySmsCredits <= 500 ? 'info' : 'green'
    },

    appAddressTypes() {
      const addressTypes = this.appConfig?.LOCATION_DATA?.AddressType
      return Array.isArray(addressTypes) && !!addressTypes.length ? addressTypes : [AddressTypeEnum.DEFAULT]
    },
    appAddressTypeDefault() {
      return this.appAddressTypes[0]
    },

    appCurrency() {
      const retVal = {
        Name  : this.appConfig.LOCATION_DATA?.Currency?.name || '',
        Symbol: getSymbolFromCurrency(this.appConfig.LOCATION_DATA?.Currency?.name) || '',
        Icon  : getIconFromCurrency(this.appConfig.LOCATION_DATA?.Currency?.name) || '',
        Locale: this.appConfig.LOCATION_DATA?.Currency?.locale || ''
      }
      return retVal
    },
    appCurrencyOptions() {
      const Locale = window.appConfig.LOCATION_DATA?.Currency?.locale || 'el-GR'
      const Currency = window.appConfig.LOCATION_DATA?.Currency?.name || 'EUR'
      return new Intl.NumberFormat(Locale, {
        style   : 'currency',
        currency: Currency
      }).resolvedOptions()
    },

    appIsPWA() {
      return this.$DataStore.app.displayMode !== 'browser'
    },

    appIsAndroidPWA() {
      return /android/i.test(navigator.userAgent) && this.appIsPWA
    },

    appMachines() {
      return window.Vue.AppMachines ? window.Vue.AppMachines : []
    },

    appMarketingTemplates() {
      return window.Vue.AppMarketingTemplates ? window.Vue.AppMarketingTemplates : []
    },

    appMachineGroups() {
      return window.Vue.AppMachineGroups ? window.Vue.AppMachineGroups : []
    },

    appMachineGroupsActive() {
      return this.appMachineGroups.filter(group => group.Status)
    },

    appMachineOwnGroups() {
      return this.appMachineGroupsActive?.filter(group => group.Guid.includes(this.$DataStore?.app?.guid))?.map(group => String(group.Id)) || []
    },

    appMachinePrinters() {
      return window.Vue.AppMachinePrinters ? window.Vue.AppMachinePrinters : []
    },
    appMachinePrintersActive() {
      return isDesktopApp() ? this.appMachinePrinters.filter(printer => printer.status === '1') : [{
        name  : '',
        copies: 1
      }]
    },
    appMachineWaiterPrintersActive() {
      return this.appMachinePrintersActive?.filter(printer => printer?.waiter === '1') || [{
        name  : '',
        copies: 1
      }]
    },
    appHasPrinters() {
      return isDesktopApp() ? this.appMachinePrinters.filter(printer => printer.status === '1').length > 0 : true
    },

    appMachineFiscalDocPrinters() {
      return window.Vue.AppMachineFiscalDocPrinters ? window.Vue.AppMachineFiscalDocPrinters : []
    },
    appMachineFiscalDocPrintersActive() {
      return isDesktopApp() ? this.appMachineFiscalDocPrinters.filter(printer => printer.status === '1') : [{
        name  : '',
        copies: 1
      }]
    },
    appMachineHasFiscalDocPrinters() {
      return this.appMachineFiscalDocPrintersActive.length && this.appHasFiscalDocPrinters
    },
    appHasFiscalDocPrinters() {
      return isDesktopApp() ? this.appMachineFiscalDocPrinters.filter(printer => printer.status === '1').length > 0 : true
    },

    appMachineFiscalDocTotalsZPrinters() {
      return window.Vue.AppMachineFiscalDocTotalsZPrinters ? window.Vue.AppMachineFiscalDocTotalsZPrinters : []
    },
    appMachineFiscalDocTotalsZPrintersActive() {
      return isDesktopApp() ? this.appMachineFiscalDocTotalsZPrinters.filter(printer => printer.status === '1') : [{
        name  : '',
        copies: 1
      }]
    },
    appMachineHasFiscalDocTotalsZPrinters() {
      return this.appMachineFiscalDocTotalsZPrintersActive.length && this.appHasFiscalDocPrinters
    },
    appHasFiscalDocTotalsZPrinters() {
      return isDesktopApp() ? this.appMachineFiscalDocTotalsZPrinters.filter(printer => printer.status === '1').length > 0 : true
    },

    appMachineMarketingPrinters() {
      return window.Vue.AppMachineMarketingPrinters ? window.Vue.AppMachineMarketingPrinters : []
    },
    appMachineMarketingPrintersActive() {
      return isDesktopApp() ? this.appMachineMarketingPrinters.filter(printer => printer.status === '1') : [{
        name  : '',
        copies: 1
      }]
    },
    appMachineHasMarketingPrinters() {
      return this.appMachineMarketingPrintersActive.length && this.appHasFiscalDocPrinters
    },
    appHasMarketingPrinters() {
      return isDesktopApp() ? this.appMachineMarketingPrinters.filter(printer => printer.status === '1').length > 0 : true
    },

    appPrinterGroups() {
      return window.Vue.AppPrinterGroups ? window.Vue.AppPrinterGroups : []
    },
    appPrinterGroupsActive() {
      return this.appPrinterGroups?.filter(group => group.Status) || []
    },

    appMachinePreparationPrinters() {
      return window.Vue.AppMachinePreparationPrinters ? window.Vue.AppMachinePreparationPrinters : []
    },
    appMachinePreparationPrintersActive() {
      return isDesktopApp() ? this.appMachinePreparationPrinters.filter(printer => printer.status === '1') : [{
        name  : '',
        copies: 1
      }]
    },
    appHasPreparationPrinters() {
      return isDesktopApp() ? this.appMachinePreparationPrinters.filter(printer => printer.status === '1').length > 0 : true
    },

    domainReseller() {
      return this.appConfig?.LOCATION_DATA?.Reseller || null
    },

    defaultLanguage() {
      return this.selectedLanguages?.find(lang => lang.Selected) || this.selectedLanguages[0]
    },
    selectedLanguages() {
      const languages = clone(this.appConfig?.LOCATION_DATA?.LanguageList || [])
      return languages.sort((a, b) => a.Selected === b.Selected ? 0 : a.Selected ? -1 : 1)
    },
    faqLanguages() {
      return this.$i18n?.languages?.filter(lang => ['el', 'en'].includes(lang.locale)) || []
    },

    multipleLocations() {
      return this.appConfig?.LOCATION_DATA?.MultipleLocations || []
    },

    scheduledOrderConfig() {
      return {
        Delivery: {
          Active                  : this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.Active || false,
          Sources                 : this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.Sources || [SourceTypeEnum.ALL],
          StartTime               : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.StartTime) || 10,
          EndTime                 : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.EndTime) || 22,
          MinimumOrderMinutesAhead: parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.MinimumOrderMinutesAhead) || 30,
          DaysAhead               : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Delivery?.DaysAhead) || 5
        },
        Collection: {
          Active                  : this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.Active || false,
          Sources                 : this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.Sources || [SourceTypeEnum.ALL],
          StartTime               : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.StartTime) || 10,
          EndTime                 : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.EndTime) || 22,
          MinimumOrderMinutesAhead: parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.MinimumOrderMinutesAhead) || 30,
          DaysAhead               : parseInt(this.appConfig?.LOCATION_DATA?.OrderScheduled?.Collection?.DaysAhead) || 5
        }
      }
    }
  },
  mounted() {
  },
  beforeDestroy() {
  }
}
